import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useAccount, useConfig } from "wagmi";
import { getWalletClient } from "wagmi/actions";
import type { Address } from "viem";
import type { RedKeel } from "../config/networks";

/**
 * Los avisos que sabe mandar el bot. MISMO ORDEN Y MISMOS NOMBRES que `AVISOS` en avisos/app.py:
 * uno que esté allí y no aquí se manda sin que nadie pueda apagarlo, y uno que esté aquí y no allí
 * enseña una casilla que no hace nada.
 *
 * Cada mensaje llega con los números de ESA piscina —par, red, tu banda, el precio de ese momento,
 * lo que te queda de gas y de reajustes—, no con una frase que valdría para cualquiera.
 */
export const TIPOS_AVISO = [
  { clave: "rango", etiqueta: "Se sale o vuelve al rango", ayuda: "Con el precio, tu banda y cuánto se salió" },
  { clave: "robot", etiqueta: "El robot reajusta", ayuda: "De qué banda a qué banda la movió" },
  { clave: "gas", etiqueta: "La bolsa de gas se vacía", ayuda: "Sin gas el robot deja de trabajar" },
  { clave: "cuota", etiqueta: "Se acaban los reajustes", ayuda: "Avisa al bajar de 25 y al llegar a 0" },
  { clave: "ganancias", etiqueta: "Se cobran comisiones", ayuda: "Cuánto se apartó y qué % es de tu capital" },
  { clave: "objetivo", etiqueta: "Llega a tu objetivo", ayuda: "La piscina se cierra sola y queda en el estable" },
  { clave: "reserva", etiqueta: "Se reinyecta el colchón", ayuda: "Cuánto entró a trabajar y cuánto queda" },
  { clave: "pausa", etiqueta: "La pausas o la reanudas", ayuda: "Confirma que el robot paró o volvió" },
] as const;

export type Avisos = Record<string, boolean>;

export type EstadoAvisos = {
  /** el @usuario del bot, o null si el servidor no tiene bot configurado */
  bot: string | null;
  conectado: boolean;
  /** hay un enlace emitido y sin usar */
  pendiente?: boolean;
  chatParcial: string | null;
  avisos: Avisos;
  desde?: number;
  /** cuántas piscinas está vigilando el bot para esta wallet, en todas las redes */
  vaultsVigilados?: number;
};

/**
 * BASE DE LA API. En produccion la sirve el propio dominio (nginx -> 127.0.0.1:8792), asi que es
 * una ruta relativa y no hace falta tocar la CSP, que solo permite `connect-src 'self'`.
 *
 * En `npm run dev` no hay nginx delante: se puede apuntar a otro sitio con VITE_AVISOS_URL, y si no
 * se apunta, las llamadas fallan y la pantalla dice que los avisos no estan disponibles — que es la
 * verdad, no un error que haya que esconder.
 */
const BASE = import.meta.env.VITE_AVISOS_URL ?? "/api/avisos";

async function pedirJson(ruta: string, cuerpo?: unknown) {
  const r = await fetch(BASE + ruta, {
    method: cuerpo === undefined ? "GET" : "POST",
    headers: cuerpo === undefined ? undefined : { "Content-Type": "application/json" },
    body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error ?? `el servidor de avisos respondio ${r.status}`);
  return d;
}

/**
 * Los avisos son DE LA WALLET, no de un vault ni de una red.
 *
 * Antes había que conectar Telegram vault por vault y red por red: quien tenía cuatro piscinas
 * firmaba cuatro veces, y la quinta que abría nacía sin avisos sin que nada se lo dijera — no
 * llegaba ningún mensaje, que es exactamente lo que se ve cuando todo va bien. Ahora se firma una
 * vez y el servicio descubre las piscinas de esa wallet en las tres redes, incluidas las futuras.
 *
 * `vault` y `red` se siguen aceptando porque la pantalla vive dentro del detalle de un vault, pero
 * ya solo sirven para firmar en la cadena correcta.
 */
export function useAvisos(_vault: Address | undefined, red: RedKeel | undefined) {
  const { address } = useAccount();
  const config = useConfig();
  const qc = useQueryClient();
  const [haciendo, setHaciendo] = useState<null | "conectar" | "guardar" | "baja">(null);
  const [error, setError] = useState<string | null>(null);
  const [enlace, setEnlace] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ["avisos", address],
    enabled: !!address,
    // Mientras hay un enlace pendiente conviene refrescar: el «conectado» llega cuando el usuario
    // pulsa Start en Telegram, en otra ventana, sin que la app se entere de nada.
    refetchInterval: (q) => ((q.state.data as EstadoAvisos | undefined)?.pendiente ? 5_000 : false),
    retry: false,
    queryFn: async (): Promise<EstadoAvisos> => pedirJson(`/estado?wallet=${address}`),
  });

  /** Pide el texto, lo firma con la wallet y devuelve lo necesario para cualquier accion.
   *
   *  Se manda TAMBIEN `wallet` y `red`: para las EOA basta el `ecrecover` del backend, pero las
   *  wallets de CONTRATO (Coinbase Smart Wallet, Safe, Argent...) firman con EIP-1271/ERC-6492 y su
   *  firma NO se recupera con ecrecover — el backend la valida llamando al propio contrato de la
   *  wallet en su red. Sin esto, esos usuarios veian "la firma no es valida". */
  async function firmar() {
    const wallet = await getWalletClient(config, { chainId: red!.idCadena });
    if (!wallet || !address) throw new Error("conecta la wallet");
    const { nonce, mensaje } = await pedirJson("/nonce", {});
    const firma = await wallet.signMessage({ account: address, message: mensaje });
    return { nonce, firma, wallet: address, red: red!.clave };
  }

  async function envolver<T>(que: typeof haciendo, fn: () => Promise<T>) {
    setError(null);
    setHaciendo(que);
    try {
      return await fn();
    } catch (e) {
      // El rechazo en la wallet no es un error que haya que gritar: es alguien diciendo que no.
      const m = e instanceof Error ? e.message : String(e);
      setError(/rejected|denied|User rejected/i.test(m) ? null : m);
      return undefined;
    } finally {
      setHaciendo(null);
      qc.invalidateQueries({ queryKey: ["avisos", address] });
    }
  }

  return {
    ...consulta,
    haciendo,
    error,
    enlace,
    limpiar: () => { setError(null); setEnlace(null); },

    conectar: () =>
      envolver("conectar", async () => {
        const { enlace: url } = await pedirJson("/vincular", await firmar());
        setEnlace(url);
        // Se abre en otra pestaña en vez de navegar: si el navegador bloquea la ventana, el enlace
        // sigue en pantalla para pulsarlo a mano.
        window.open(url, "_blank", "noopener");
        return url as string;
      }),

    guardar: (avisos: Avisos) =>
      envolver("guardar", async () => pedirJson("/avisos", { ...(await firmar()), avisos })),

    desconectar: () => envolver("baja", async () => pedirJson("/baja", await firmar())),
  };
}
